import { yupResolver } from '@hookform/resolvers/yup';
import { Row, Col, Divider, Form, Button, message } from 'antd';
import {
  useCreateDonorForUserMutation,
  AnimalSpecies,
  BloodType,
  DonorType,
  AnimalGender,
} from 'gql/types';
import { Spin } from 'modules/common/components';
import { UnsavedChangesModal } from 'modules/common/components/UnsavedChangesModal';
import { useUnsavedChangesWarning } from 'modules/common/hooks/useUnsavedChangesWarning';
import { GET_REQUEST_LIST } from 'modules/requests/graphql/queries';
import { FC, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import OptionalFormSection from './OptionalSection';
import PetFormSection from './RequiredSection';
import { createDonorSchema, CreateDonorSchemaType } from './schema';

const CreateDonorForm: FC = () => {
  const { t } = useTranslation('donor.CreateDonorForm');

  const defaultValues = useMemo<CreateDonorSchemaType>(
    () => ({
      required: {
        species: null as unknown as AnimalSpecies,
        gender: null as unknown as AnimalGender,
        weight: null as unknown as number,
        name: '',
        primaryVeterinarian: '',
        medicationCourse: '',
        location: '',
        region: '',
        userId: '',
        dateOfBirth: null as unknown as Date,
        neutered: false,
      },
      optional: {
        bloodType: BloodType.Unknown,
        teamId: '',
        donorType: DonorType.Individual,
        microchipNumber: '',
        distinguishableMarks: '',
        comment: '',
        color: '',
        breed: '',
        hasMicrochip: false,
      },
    }),
    []
  );

  const createDonorForm = useForm<CreateDonorSchemaType>({
    resolver: yupResolver(createDonorSchema),
    mode: 'onChange',
    defaultValues,
  });

  const {
    formState: { isDirty, isValid },
    handleSubmit,
    reset,
    watch,
    trigger,
    resetField,
  } = createDonorForm;

  const [createDonor, createDonorMutation] = useCreateDonorForUserMutation({
    refetchQueries: [GET_REQUEST_LIST],
    onCompleted: () => {
      message.success(t('donorCreated'));
      reset(defaultValues);
    },
    onError: (e) => {
      message.error(e.message);
    },
  });

  const isLoading = createDonorMutation.loading;

  const submitForm = async (data: CreateDonorSchemaType) => {
    const {
      bloodType,
      teamId,
      donorType,
      microchipNumber,
      distinguishableMarks,
      comment,
      color,
      breed,
      hasMicrochip,
    } = data.optional;
    const {
      dateOfBirth,
      gender,
      name,
      neutered,
      primaryVeterinarian,
      species,
      weight,
      medicationCourse,
      location,
    } = data.required;

    createDonor({
      variables: {
        input: {
          donorData: {
            dateOfBirth,
            breed,
            bloodType,
            color,
            gender,
            name,
            neutered,
            primaryVeterinarian,
            species,
            status: undefined,
            teamId,
            type: donorType,
            weight,
            distinguishableMarks,
            medicationCourse,
            microchipNumber,
            comment,
            location,
            hasMicrochip,
          },
          userId: data.required.userId,
        },
      },
    });
  };

  const createDonorHandler = handleSubmit(submitForm);

  const resetForm = () => reset(defaultValues);

  const handleSave = async () => {
    const formData = createDonorForm.getValues();
    await submitForm(formData);
  };

  const specie = watch('required.species');
  const weight = watch('required.weight');
  const userId = watch('required.userId');
  const region = watch('required.region');

  useEffect(() => {
    if (weight) {
      trigger('required.weight');
    }
  }, [specie, weight]);

  useEffect(() => {
    const subscription = watch((_value, { name }) => {
      if (name === 'required.region') {
        resetField('required.location');
      }
    });

    return () => subscription.unsubscribe();
  }, [createDonorForm, region]);

  const {
    isModalOpen,
    handleStay,
    handleDiscard,
    handleSaveAndLeave,
    canSaveAndLeave,
  } = useUnsavedChangesWarning({
    isDirty,
    onSave: handleSave,
    isFormValid: isValid,
  });

  return (
    <>
      <Spin spinning={isLoading}>
        <FormProvider {...createDonorForm}>
          <Form labelCol={{ span: 12 }} layout="vertical">
            <Row>
              <Col span={24}>
                <Row justify="space-between" gutter={[10, 0]}>
                  <Col sm={12} span={24}>
                    <PetFormSection fieldNamePrefix="required" />
                  </Col>
                  <Col sm={0} span={24}>
                    <Divider />
                  </Col>
                  <Col sm={12} span={24}>
                    <OptionalFormSection
                      fieldNamePrefix="optional"
                      userId={userId}
                    />
                  </Col>
                </Row>
              </Col>
            </Row>
            <Row justify="space-between">
              <Col>
                <Button
                  onClick={resetForm}
                  disabled={!createDonorForm.formState.isDirty}
                >
                  {t('cancel')}
                </Button>
              </Col>
              <Col>
                <Button
                  type="primary"
                  disabled={!createDonorForm.formState.isValid}
                  onClick={createDonorHandler}
                >
                  {t('save')}
                </Button>
              </Col>
            </Row>
          </Form>
        </FormProvider>
      </Spin>

      <UnsavedChangesModal
        isOpen={isModalOpen}
        onStay={handleStay}
        onDiscard={handleDiscard}
        onSaveAndLeave={handleSaveAndLeave}
        canSaveAndLeave={canSaveAndLeave}
        isSaving={isLoading}
      />
    </>
  );
};

export default CreateDonorForm;
